

























































import { Component, Mixins, Ref } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";

import DetailsModal from "@/components/DetailsModal.vue";
import DialogModal from "@/components/DialogModal.vue";

import ReportTemplateListMixin from "@/mixins/ReportTemplateListMixin";
import { ReportBuilderTabKey } from "@/mixins/ReportBuilder/ReportBuilderMixin";
import { AdcontrolProfile, ReportTemplate } from "@/models";
import {
  TEMPLATES_DELETED_MESSAGE,
  templateDeletedMessage,
  templateDuplicatedMessage,
} from "@/components/Agencies/ReportBuilder/reportTemplate";
import TemplateDeleteModal from "@/components/Agencies/ReportBuilder/TemplateDeleteModal.vue";
import TemplateDuplicateModal from "@/components/Agencies/ReportBuilder/TemplateDuplicateModal.vue";
import ToastInvoker from "@/utils/ToastInvoker";

import SettingsList from "@/components/Settings/SettingsList.vue";
import {
  SettingsListAction,
  SettingsListActionEvent,
  SettingsListColumn,
} from "@/components/Settings/settingsListTypes";
import TemplateCompetitorDetailsTable from "@/components/Agencies/ReportBuilder/TemplateCompetitorDetailsTable.vue";

@Component({
  components: {
    SettingsList,
    DetailsModal,
    DialogModal,
    TemplateCompetitorDetailsTable,
    TemplateDeleteModal,
    TemplateDuplicateModal,
  },
})
export default class ReportTemplatesList extends Mixins(
  ReportTemplateListMixin
) {
  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Mutation("automaticReportsCache/invalidateAutomaticReportsCache")
  readonly invalidateAutomaticReportsCache!: () => void;

  @Ref("list") readonly list?: SettingsList;
  @Ref("deleteModal") readonly deleteModal!: TemplateDeleteModal;
  @Ref("duplicateModal") readonly duplicateModal!: TemplateDuplicateModal;

  pendingDeleteTargets: ReportTemplate[] = [];
  pendingBulkDelete = false;
  templateToDuplicate: ReportTemplate | null = null;

  readonly columns: SettingsListColumn<ReportTemplate>[] = [
    {
      key: "name",
      label: "nome",
      col: "col-5",
      cellClass: "text-truncate font-weight-bold",
      value: (template) => this.displayName(template),
    },
    {
      key: "profile",
      label: "profilo",
      col: "col-5",
      cellClass: "text-truncate text-muted",
      value: (template) => this.profileName(template),
    },
  ];

  readonly actions: SettingsListAction<ReportTemplate>[] = [
    {
      key: "details",
      icon: "eye",
      iconV2: true,
      title: "Dettagli",
      run: (template) => this.openDetails(template),
    },
    {
      key: "edit",
      icon: "edit-02",
      iconV2: true,
      title: "Modifica",
      disabled: (template) => !this.belongsToProfile(template),
      run: (template) => this.onEdit(template),
    },
    {
      key: "duplicate",
      icon: "copy-03",
      iconV2: true,
      title: "Duplica",
      disabled: (template) => !this.belongsToProfile(template),
      run: (template) => this.onDuplicate(template),
    },
    {
      key: "delete",
      icon: "trash-01",
      iconV2: true,
      title: "Elimina",
      variant: "danger",
      disabled: (template) => !this.belongsToProfile(template),
      run: (template) => this.onDelete(template),
    },
  ];

  rowClass(template: ReportTemplate): string {
    return this.belongsToProfile(template)
      ? ""
      : "settings-list__item--disabled";
  }

  protected onTemplateListReset() {
    this.list?.reset();
  }

  onAction({ key, item }: SettingsListActionEvent<ReportTemplate>) {
    this.actions.find((a) => a.key === key)?.run(item);
  }

  // --- Duplicate -----------------------------------------------------------

  onDuplicate(template: ReportTemplate) {
    this.templateToDuplicate = template;
    this.$nextTick(() => this.duplicateModal.show());
  }

  async onDuplicateConfirmed(name: string) {
    const target = this.templateToDuplicate;
    if (!target) return;
    if (!(await this.duplicateTemplate(target, name))) return;
    this.duplicateModal.hide();
    this.templateToDuplicate = null;
    ToastInvoker.successToast(templateDuplicatedMessage(target, name));
  }

  // --- Delete --------------------------------------------------------------

  onDelete(template: ReportTemplate) {
    this.pendingDeleteTargets = [template];
    this.pendingBulkDelete = false;
    this.deleteModal.show();
  }

  onBulkDelete(ids: string[]) {
    if (!ids.length) return;
    this.pendingDeleteTargets = this.templates.filter((t) =>
      ids.includes(t.id)
    );
    this.pendingBulkDelete = true;
    this.deleteModal.show();
  }

  async onDeleteConfirmed() {
    const targets = this.pendingDeleteTargets;
    const bulk = this.pendingBulkDelete;
    if (!targets.length) return;

    this.deleting = true;
    try {
      const ids = targets.map((t) => t.id);
      const destroyed = await this.deleteTemplates(ids, bulk);
      this.deleteModal.hide();
      if (!destroyed) return;

      if (bulk) this.list?.exitSelectionMode();
      this.invalidateAutomaticReportsCache();
      ToastInvoker.successToast(
        bulk ? TEMPLATES_DELETED_MESSAGE : templateDeletedMessage(targets[0])
      );
    } finally {
      this.deleting = false;
      this.pendingDeleteTargets = [];
    }
  }

  // --- Navigation ----------------------------------------------------------

  onNew() {
    const query: Record<string, string> = { newTemplate: "1" };
    if (this.profile?.slug) query.profile = this.profile.slug;
    this.$router.push({ name: "custom-report", query });
  }

  async onEdit(template: ReportTemplate) {
    if (await this.warnIfEditBlocked(template)) return;

    const query: Record<string, string> = {
      tab: ReportBuilderTabKey.ReportTemplate,
      editTemplate: template.id,
    };
    if (this.profile?.slug) query.profile = this.profile.slug;
    this.$router.push({ name: "custom-report", query });
  }
}
